/**
 * Pure helpers for recurring presets (tracker item 76): the editor's form and
 * request body, a one-line description, and how a preset pre-fills the New
 * recurring schedule form. A preset has no start date, customer or amount.
 */

import * as types from '../api/types';
import { describeRecurrence, frequencyConfigError } from '../utils/recurrence';
import {
  BILLING_FREQUENCY_LABELS,
  BillingForm,
  MonthlyMode,
  RecurrenceForm,
  buildFrequencyDetails,
  defaultBillingForm,
  firstPatternDate,
  parseInterval,
} from './recurringUi';

export interface PresetForm {
  name: string;
  frequency: types.RecurringFrequency;
  /** Text from the number input */
  interval: string;
  daysOfWeek: number[];
  monthlyMode: MonthlyMode;
  dayOfMonth: string;
  ordinal: string;
  weekday: string;
  billingModel: types.RecurringBillingModel;
  billingFrequency: types.FlatContractBillingFrequency;
  billingInterval: string;
  notes: string;
  isActive: boolean;
}

export function defaultPresetForm(): PresetForm {
  return {
    name: '',
    frequency: 'WEEKLY',
    interval: '1',
    daysOfWeek: [1],
    monthlyMode: 'DAY_OF_MONTH',
    dayOfMonth: '1',
    ordinal: '1',
    weekday: '1',
    billingModel: 'PER_VISIT',
    billingFrequency: 'MONTHLY',
    billingInterval: '1',
    notes: '',
    isActive: true,
  };
}

const isFrequency = (value: string): value is types.RecurringFrequency => (types.RECURRING_FREQUENCIES as readonly string[]).includes(value);

/** The editor form for a stored preset. */
export function presetFormFrom(preset: types.RecurringPreset): PresetForm {
  const base = defaultPresetForm();
  const details = preset.frequencyDetails;
  return {
    ...base,
    name: preset.name,
    frequency: isFrequency(preset.frequency) ? preset.frequency : 'WEEKLY',
    interval: String(preset.interval),
    daysOfWeek: details?.daysOfWeek ? [...details.daysOfWeek] : [],
    monthlyMode: details?.ordinal !== undefined ? 'NTH_WEEKDAY' : 'DAY_OF_MONTH',
    dayOfMonth: details?.dayOfMonth !== undefined ? String(details.dayOfMonth) : base.dayOfMonth,
    ordinal: details?.ordinal !== undefined ? String(details.ordinal) : base.ordinal,
    weekday: details?.dayOfWeek !== undefined ? String(details.dayOfWeek) : base.weekday,
    billingModel: preset.billingModel,
    billingFrequency: preset.billingFrequency ?? base.billingFrequency,
    billingInterval: String(preset.billingInterval || 1),
    notes: preset.notes ?? '',
    isActive: preset.isActive,
  };
}

/** The pattern fields of the form in the schedule form's shape (no dates), to reuse its detail builders. */
function patternOf(form: PresetForm): RecurrenceForm {
  return {
    frequency: form.frequency,
    interval: form.interval,
    daysOfWeek: form.daysOfWeek,
    monthlyMode: form.monthlyMode,
    dayOfMonth: form.dayOfMonth,
    ordinal: form.ordinal,
    weekday: form.weekday,
    startDate: '',
    endDate: '',
  };
}

export type PresetFormErrors = Partial<Record<'name' | 'interval' | 'pattern' | 'billingInterval', string>>;

export function presetFormErrors(form: PresetForm): PresetFormErrors {
  const errors: PresetFormErrors = {};
  const name = form.name.trim();
  if (!name) errors.name = 'Enter a name.';
  else if (name.length > 100) errors.name = 'Use 100 characters or fewer.';

  const interval = parseInterval(form.interval);
  if (!Number.isInteger(interval) || interval < 1) errors.interval = 'Enter a whole number of 1 or more.';
  else if (interval > 99) errors.interval = 'Enter 99 or less.';

  if (form.frequency === 'MONTHLY' && form.monthlyMode === 'DAY_OF_MONTH' && !/^\d+$/.test(form.dayOfMonth.trim())) {
    errors.pattern = 'Day of the month must be 1 to 31.';
  } else {
    const problem = frequencyConfigError(form.frequency, Number.isInteger(interval) && interval >= 1 ? interval : 1, buildFrequencyDetails(patternOf(form)));
    if (problem) errors.pattern = problem;
  }

  if (form.billingModel === 'FLAT_CONTRACT') {
    const every = parseInterval(form.billingInterval);
    if (!Number.isInteger(every) || every < 1 || every > 120) errors.billingInterval = 'Enter a whole number from 1 to 120.';
  }
  return errors;
}

/** The POST / PATCH body. Blank notes are sent as null so an edit can clear them. */
export function buildPresetRequest(form: PresetForm): types.CreateRecurringPresetRequest {
  const flat = form.billingModel === 'FLAT_CONTRACT';
  return {
    name: form.name.trim(),
    frequency: form.frequency,
    interval: parseInterval(form.interval),
    frequencyDetails: buildFrequencyDetails(patternOf(form)) ?? null,
    billingModel: form.billingModel,
    billingFrequency: flat ? form.billingFrequency : null,
    billingInterval: flat ? parseInterval(form.billingInterval) : 1,
    notes: form.notes.trim() || null,
    isActive: form.isActive,
  };
}

/** "Weekly on Mon · Per visit" / "Every 2 months on the 1st · Flat contract, billed monthly" */
export function describePreset(preset: Pick<types.RecurringPreset, 'frequency' | 'interval' | 'frequencyDetails' | 'billingModel' | 'billingFrequency' | 'billingInterval'>): string {
  const pattern = describeRecurrence(preset.frequency, preset.interval, preset.frequencyDetails);
  if (preset.billingModel !== 'FLAT_CONTRACT') return `${pattern} · Per visit`;
  const period = preset.billingFrequency ? BILLING_FREQUENCY_LABELS[preset.billingFrequency].toLowerCase() : 'per period';
  return `${pattern} · Flat contract, billed ${preset.billingInterval > 1 ? `every ${preset.billingInterval} periods (${period})` : period}`;
}

// ---- "Start from a preset" on the New recurring schedule page ----

/**
 * The schedule form with the preset's pattern. The dates stay the user's: the
 * start date only moves forward to the first date on the new pattern (the
 * backend lists the start date itself as the first visit), and an end date
 * before it is cleared.
 */
export function recurrenceFromPreset(preset: types.RecurringPreset, current: RecurrenceForm): RecurrenceForm {
  const details = preset.frequencyDetails;
  const next: RecurrenceForm = {
    ...current,
    frequency: isFrequency(preset.frequency) ? preset.frequency : current.frequency,
    interval: String(preset.interval),
    daysOfWeek: details?.daysOfWeek ? [...details.daysOfWeek] : current.daysOfWeek,
    monthlyMode: details?.ordinal !== undefined ? 'NTH_WEEKDAY' : details?.dayOfMonth !== undefined ? 'DAY_OF_MONTH' : current.monthlyMode,
    dayOfMonth: details?.dayOfMonth !== undefined ? String(details.dayOfMonth) : current.dayOfMonth,
    ordinal: details?.ordinal !== undefined ? String(details.ordinal) : current.ordinal,
    weekday: details?.dayOfWeek !== undefined ? String(details.dayOfWeek) : current.weekday,
  };
  const first = firstPatternDate(next);
  if (first) next.startDate = first;
  if (next.endDate && next.startDate && next.endDate < next.startDate) next.endDate = '';
  return next;
}

/** The billing form for the preset's terms (the contract amount is entered on the schedule). */
export function billingFromPreset(preset: types.RecurringPreset): BillingForm {
  const base = defaultBillingForm();
  if (preset.billingModel !== 'FLAT_CONTRACT') return base;
  return { model: 'FLAT_CONTRACT', amount: '', frequency: preset.billingFrequency ?? base.frequency, interval: String(preset.billingInterval || 1) };
}
